import { useState, useEffect } from "react";
import {
  Card,
  Button,
  message,
  Tag,
  Popconfirm,
  Image,
  Empty,
  Spin,
  Row,
  Col,
  Typography,
  Space,
} from "antd";
import {
  PlusOutlined,
  CheckCircleOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import sellerTypologiesQuery from "../queries/sellerTypologiesQuery";
import allTypologiesQuery from "../queries/allTypologiesQuery";
import addTypologyToSellerMutation from "../queries/addTypologyToSellerMutation";
import removeTypologyFromSellerMutation from "../queries/removeTypologyFromSellerMutation";

const { Text } = Typography;

const MarketplaceSellerTypologies = ({ seller, onUpdate }) => {
  const [typologiesLoading, setTypologiesLoading] = useState(true);
  const [allTypologiesLoading, setAllTypologiesLoading] = useState(true);
  const [typologies, setTypologies] = useState([]);
  const [allTypologies, setAllTypologies] = useState([]);
  const [addingTypology, setAddingTypology] = useState(null);
  const [removingTypology, setRemovingTypology] = useState(null);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  // Fetch seller's typologies
  const fetchSellerTypologies = async (isInitial = false) => {
    if (isInitial) setTypologiesLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const response = await axios.post(
        graphqlUrl,
        {
          query: sellerTypologiesQuery,
          variables: { sellerId: seller.objectId },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const edges = response.data?.marketplace_seller?.typologies?.edges || [];
      const sellerTypologies = edges.map((edge) => edge.node);
      setTypologies(sellerTypologies);
    } catch (error) {
      console.error("Error fetching seller typologies:", error);
      message.error("Errore durante il caricamento delle tipologie");
    } finally {
      if (isInitial) setTypologiesLoading(false);
    }
  };

  // Fetch all available typologies
  const fetchAllTypologies = async (isInitial = false) => {
    if (isInitial) setAllTypologiesLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const response = await axios.post(
        graphqlUrl,
        {
          query: allTypologiesQuery,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const edges = response.data?.marketplace_typologies?.edges || [];
      const typologiesList = edges.map((edge) => edge.node);
      setAllTypologies(typologiesList);
    } catch (error) {
      console.error("Error fetching all typologies:", error);
      message.error("Errore durante il caricamento delle tipologie disponibili");
    } finally {
      if (isInitial) setAllTypologiesLoading(false);
    }
  };

  // Add typology to seller
  const handleAddTypology = async (typologyId) => {
    setAddingTypology(typologyId);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      await axios.post(
        graphqlUrl,
        {
          query: addTypologyToSellerMutation,
          variables: {
            sellerId: seller.objectId,
            typologyId: typologyId,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success("Tipologia aggiunta con successo");
      fetchSellerTypologies();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error adding typology:", error);
      message.error("Errore durante l'aggiunta della tipologia");
    } finally {
      setAddingTypology(null);
    }
  };

  // Remove typology from seller
  const handleRemoveTypology = async (typologyId) => {
    setRemovingTypology(typologyId);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      await axios.post(
        graphqlUrl,
        {
          query: removeTypologyFromSellerMutation,
          variables: {
            sellerId: seller.objectId,
            typologyId: typologyId,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success("Tipologia rimossa con successo");
      fetchSellerTypologies();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error removing typology:", error);
      message.error("Errore durante la rimozione della tipologia");
    } finally {
      setRemovingTypology(null);
    }
  };

  useEffect(() => {
    fetchSellerTypologies(true);
    fetchAllTypologies(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller.objectId]);

  // Check if a typology is already linked
  const isTypologyLinked = (typologyId) => {
    return typologies.some((typ) => typ.objectId === typologyId);
  };

  // Filter typologies based on seller's b2b/b2c configuration
  const getAvailableTypologies = () => {
    return allTypologies.filter((typ) => {
      // Typology must not be already linked
      if (isTypologyLinked(typ.objectId)) return false;

      // Typology must match at least one of seller's modes
      if (seller.b2b && typ.b2b) return true;
      if (seller.b2c && typ.b2c) return true;

      return false;
    });
  };

  const availableTypologies = getAvailableTypologies();

  return (
    <div>
      {/* Linked Typologies */}
      <Card title="Tipologie Associate" style={{ marginBottom: 24 }}>
        {typologiesLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
          </div>
        ) : typologies.length === 0 ? (
          <Empty
            description="Nessuna tipologia associata"
            style={{ padding: "20px" }}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {typologies.map((typology) => (
              <Col xs={24} sm={12} md={8} lg={6} key={typology.objectId}>
                <Card
                  hoverable
                  style={{ textAlign: "center" }}
                  cover={
                    typology.icon?.url ? (
                      <div style={{ padding: "20px 20px 0" }}>
                        <Image
                          src={typology.icon.url}
                          alt={typology.name}
                          height={80}
                          style={{ objectFit: "contain" }}
                          preview={false}
                        />
                      </div>
                    ) : (
                      <div style={{
                        height: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f0f0f0"
                      }}>
                        <Text type="secondary">Nessuna icona</Text>
                      </div>
                    )
                  }
                  actions={[
                    <Popconfirm
                      title="Rimuovere questa tipologia?"
                      onConfirm={() => handleRemoveTypology(typology.objectId)}
                      okText="Si"
                      cancelText="No"
                      key="remove"
                    >
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        loading={removingTypology === typology.objectId}
                        size="small"
                      >
                        Rimuovi
                      </Button>
                    </Popconfirm>
                  ]}
                >
                  <Card.Meta
                    title={typology.name}
                    description={
                      <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <Tag color="green" icon={<CheckCircleOutlined />}>
                          Associata
                        </Tag>
                        <Space size="small">
                          {typology.b2b && <Tag color="blue">B2B</Tag>}
                          {typology.b2c && <Tag color="cyan">B2C</Tag>}
                        </Space>
                      </Space>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* Available Typologies */}
      <Card title="Tipologie Disponibili">
        {allTypologiesLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
          </div>
        ) : allTypologies.length === 0 ? (
          <Empty
            description={
              <span>
                Nessuna tipologia nel sistema.{" "}
                <a href="/funeralcloud/typologies">Crea delle tipologie</a> prima di poterle associare.
              </span>
            }
            style={{ padding: "20px" }}
          />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {availableTypologies.map((typology) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={typology.objectId}>
                    <Card
                      hoverable
                      style={{
                        textAlign: "center",
                        opacity: isTypologyLinked(typology.objectId) ? 0.5 : 1
                      }}
                      cover={
                        typology.icon?.url ? (
                          <div style={{ padding: "20px 20px 0" }}>
                            <Image
                              src={typology.icon.url}
                              alt={typology.name}
                              height={80}
                              style={{ objectFit: "contain" }}
                              preview={false}
                            />
                          </div>
                        ) : (
                          <div style={{
                            height: 100,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f0f0f0"
                          }}>
                            <Text type="secondary">Nessuna icona</Text>
                          </div>
                        )
                      }
                    >
                      <Card.Meta
                        title={typology.name}
                        description={
                          <Space direction="vertical" size="small" style={{ width: "100%" }}>
                            <Space size="small">
                              {typology.b2b && <Tag color="blue">B2B</Tag>}
                              {typology.b2c && <Tag color="cyan">B2C</Tag>}
                            </Space>
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={() => handleAddTypology(typology.objectId)}
                              loading={addingTypology === typology.objectId}
                              block
                            >
                              Aggiungi
                            </Button>
                          </Space>
                        }
                      />
                    </Card>
                  </Col>
                ))}
            </Row>

            {availableTypologies.length === 0 && (
              <Empty
                description={
                  allTypologies.filter(typ => !isTypologyLinked(typ.objectId)).length > 0
                    ? `Nessuna tipologia disponibile per questo venditore ${seller.b2b ? 'B2B' : ''} ${seller.b2c ? 'B2C' : ''}`
                    : "Tutte le tipologie compatibili sono già associate"
                }
                style={{ padding: "40px" }}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default MarketplaceSellerTypologies;
